import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Select, Input, message } from 'antd';
import { CallLog, OrgIntegrationService } from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { callEndedDetailsChanged } from 'shared/store/realtime-slice';
import { useApiLoader } from 'shared/hooks';
import {
  getHubspotActivityTypes,
  getHubspotCallDispositions,
  getHubspotSettings,
  postCallLog,
} from 'shared/api';
import { Icon } from 'shared/components/Icon';
import { formatDuration } from 'shared/utils/helpers';
import { makeLog } from 'shared/utils/log';
import { selectActiveCrmIntegrations } from 'shared/store/auth-slice';
import { HubspotActivityType, HubspotSettings } from 'types/hubspot';
import './style.less';

const log = makeLog('CallLogModal');

export const CallLogModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { callEndedDetails } = useAppSelector(s => s.realtime);
  const [callLog, setCallLog] = useState<CallLog>({
    sessionId: callEndedDetails?.session.id || '',
    visitorId: callEndedDetails?.visitor.id || '',
    hubspotCallingDispositionGuid: '',
  });
  const crmIntegrations = useAppSelector(selectActiveCrmIntegrations);
  const hasHubspotIntegration = crmIntegrations.find(
    i => i.service === OrgIntegrationService.hubspot,
  );
  const dispositions = useApiLoader(getHubspotCallDispositions);
  const activityTypes = useApiLoader(getHubspotActivityTypes);

  const postCallLogFn = useCallback(() => postCallLog({ callLog }), [callLog]);
  const sendCallLog = useApiLoader(postCallLogFn, true);

  const handleClose = useCallback(() => {
    dispatch(callEndedDetailsChanged(null));
  }, [dispatch]);

  const [hubspotSettings, setHubspotSettings] = useState<HubspotSettings | null>(null);
  const fetchHubspotSettings = useCallback(async () => {
    try {
      const { data } = await getHubspotSettings();
      setHubspotSettings(data);
    } catch (err) {
      log.error('Hubspot settings fetch error', err);
    }
  }, [setHubspotSettings]);
  const filteredActivityTypes =
    hubspotSettings?.logCallTypeSelectedSet.length === 0
      ? activityTypes.data || []
      : (activityTypes.data || []).filter((t: HubspotActivityType) =>
          (hubspotSettings?.logCallTypeSelectedSet || []).includes(t.value),
        );

  useEffect(() => {
    fetchHubspotSettings();
  }, [fetchHubspotSettings]);

  useEffect(
    function closeOnSave() {
      if (!sendCallLog.isLoading && sendCallLog.hasLoaded) {
        message.success('Saved call log');
        handleClose();
      }
    },
    [sendCallLog.hasLoaded, sendCallLog.isLoading, handleClose],
  );

  useEffect(
    function sendCallLogError() {
      if (sendCallLog.error) {
        log.error(`Failed to save call log: ${sendCallLog.error}`);
        message.error(`Failed to save call log: ${sendCallLog.error}`);
      }
    },
    [sendCallLog.error],
  );

  const setCallLogFromPartial = (partialCallLog: Partial<CallLog>) => {
    if (callEndedDetails) {
      setCallLog({
        ...callLog,
        sessionId: callEndedDetails.session.id,
        visitorId: callEndedDetails.visitor.id,
        ...partialCallLog,
      });
    }
  };

  const v = callEndedDetails?.visitor;
  const t1 = callEndedDetails?.visitorInteraction.startTime || Date.now() - 1000;
  const t2 = callEndedDetails?.visitorInteraction.lastSeen || Date.now();
  const duration = formatDuration(t2 - t1);

  return (
    <Modal
      visible={!!callEndedDetails && !!hasHubspotIntegration}
      className="CallLogModal"
      onCancel={handleClose}
      destroyOnClose
      title={
        <>
          <b>Log call</b>
          <div className="CallLogModal__duration">
            <Icon icon="clock" />
            {duration}
          </div>
        </>
      }
      footer={
        <>
          <Button
            className="CallLogModal__cancel"
            onClick={handleClose}
            disabled={sendCallLog.isLoading}
          >
            Skip
          </Button>
          <Button
            className="CallLogModal__submit"
            type="primary"
            disabled={!(callLog.hubspotCallingDispositionGuid || callLog.notes)}
            loading={sendCallLog.isLoading}
            onClick={() => sendCallLog.loadData()}
          >
            Log call
          </Button>
        </>
      }
    >
      {v && (
        <div className="CallLogModal__visitor">
          <b>{v.contact?.name || v.contact?.email || 'Unknown'}</b>
          <div>{v.contact?.company?.name || 'Unknown company'}</div>
          <div>
            <Icon icon="pin" /> {v.lastLocation || 'Unknown location'}
          </div>
        </div>
      )}

      <div className="CallLogModal__inputWrap">
        {hasHubspotIntegration && (
          <div>
            <div className="CallLogModal__inputHeader">
              <b>Disposition</b>
              <a
                href="https://knowledge.hubspot.com/calling/create-custom-call-and-meeting-outcomes"
                target="_blank"
                rel="noreferrer"
              >
                Manage in Hubspot
              </a>
            </div>
            <Select
              className="CallLogModal__dispositions"
              placeholder="Select disposition..."
              size="large"
              disabled={!dispositions.data || sendCallLog.isLoading}
              loading={dispositions.isLoading}
              options={(dispositions.data || []).map(d => ({
                value: d.id,
                label: d.label,
              }))}
              onChange={v =>
                setCallLogFromPartial({ hubspotCallingDispositionGuid: String(v) })
              }
            />
            {hubspotSettings?.logCallTypeMode === 'selected' && (
              <>
                <div className="CallLogModal__inputHeader">
                  <b>Meeting Type</b>
                </div>
                <Select
                  className="CallLogModal__dispositions"
                  placeholder="Select meeting type..."
                  size="large"
                  disabled={!activityTypes.data || sendCallLog.isLoading}
                  loading={activityTypes.isLoading}
                  options={filteredActivityTypes.map((d: HubspotActivityType) => ({
                    value: d.value,
                    label: d.label,
                  }))}
                  onChange={v =>
                    setCallLogFromPartial({ hubspotActivityType: String(v) })
                  }
                />
              </>
            )}
          </div>
        )}
        <div>
          <div className="CallLogModal__inputHeader">
            <b>Notes</b>
          </div>
          <Input.TextArea
            className="CallLogModal__notes"
            placeholder="Log notes about your call..."
            rows={6}
            disabled={sendCallLog.isLoading}
            onChange={evt => setCallLogFromPartial({ notes: evt.target.value })}
          />
        </div>
      </div>
    </Modal>
  );
};
